// Chakra imports
import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  SimpleGrid,
  Text,
  Tooltip,
  useToast,
} from '@chakra-ui/react';

import TableComponent from 'components/TableComponent';
import useAuth from 'contexts/useAuth';
import { queryClient } from 'index';
import { FC, useMemo, useState } from 'react';
import { FaTrash } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { Column } from 'react-table';
import { deleteWithdrawal, findAllWithdrawers } from 'services/api.service';
import CreateWithdrawel from './components/CreateWithdrawel';
import EditWithdrawel from './components/EditWithdrawel';
import CreatePaymentQrcode from './components/PaymentQRcode';
import SeeWithdrawel from './components/SeeWithdrawel';
import TransferInternal from './components/TransferInternal';
import WithdrawPix from './components/WithdrawPix';
import BarcodePayment from './components/BarcodePayment';
import { ChevronDownIcon } from '@chakra-ui/icons';
import CreateFavored from './components/CreateFavored';
import { maskCurrency } from 'utils/number';
import { BilletBeneficiary, formatRecipientDocument, getWithdrawalRecipient } from './withdrawalRecipient';

export interface CustomerAccounts {
  account: string;
  agency: string;
  bankIspb: string;
  accountHolderDocument: string;
  accountHolder: string;
  pix: string;
  id: string;
}

export interface Customer {
  id: string;
  name: string;
  document_number: string;
  accountNumber: string;
}

export interface WithdrawalApproval {
  id: string;
  withdrawalId: string;
  approverCustomerId: string;
  status: 'APPROVED' | 'REJECTED';
  observation?: string;
  createdAt: string;
  updatedAt: string;
  approver?: {
    id: string;
    name: string;
    email: string;
  };
}

export interface Withdrawal {
  amount: string;
  createdAt: string;
  fee: string;
  description: string;
  status: 'PENDING' | 'PENDING_APPROVAL' | 'IN_PAYMENT' | 'FINISHED' | 'CANCELED';
  customerId: string;
  billetCode: string;
  type: 'BILLET' | 'DICT' | 'MANU' | 'QRCODE' | 'INTERNAL';
  qrCode: string;
  id: string;
  imageUrl?: string;
  dueDate?: string;
  CustomerAccounts?: CustomerAccounts;
  Customer: Customer & {
    withdrawalApprovalsRequired?: number;
  };
  WithdrawelSulcredi: WithdrawelSulcredi[];
  withdrawSulcredi: WithdrawSulcredi;
  endToEndId?: string;
  receiver?: {
    name: string;
    document_number: string;
    accountNumber: string;
  };
  /** Favorecido/beneficiário gravado no saque na criação. */
  receiverName?: string | null;
  receiverDocument?: string | null;
  pixKeyType?: string | null;
  /** Nome do banco da conta de destino (Pix manual), resolvido pelo ISPB. Só no detalhe. */
  receiverBankName?: string | null;
  /** Beneficiário do boleto pela consulta mais recente; null fora de boleto. */
  billetBeneficiary?: BilletBeneficiary | null;
  WithdrawalApprovals?: WithdrawalApproval[];
  batchId?: string | null;
}

export interface WithdrawSulcredi {
  endToEndId?: string;
  status: number;
  updatedAt: string;
  pixKey: string;
  type?: string;
  debtorAccount?: DebtorAccount2;
  creditorAccount?: CreditorAccount2;
}

export interface DebtorAccount2 {
  ispb: string;
  ispbName: string;
  name: string;
  document?: string;
  branch?: string;
  account?: string;
  accountType?: string;
}

export interface CreditorAccount2 {
  ispb: string;
  ispbName: string;
  document: string;
  name: string;
}
export interface WithdrawelSulcredi {
  id: string;
  withdrawelId: string;
  status: number;
  endToEndId: string;
  errorCode: any;
  message: any;
  payeeName: any;
  payeeDocument: any;
  amount: string;
  integrationId: string;
  payload: PayloadSulcredi;
  createdAt: string;
  updatedAt: string;
}

export interface PayloadSulcredi {
  id: number;
  txId: any;
  pixKey: string;
  status: string;
  payment: Payment;
  refunds: any[];
  createdAt: string;
  errorCode: any;
  endToEndId: string;
  webhookType: string;
  debtorAccount: DebtorAccount;
  idempotencyKey: string;
  creditDebitType: string;
  creditorAccount: CreditorAccount;
  localInstrument: string;
  transactionType: string;
  remittanceInformation: any;
}

export interface Payment {
  amount: string;
  currency: string;
}

export interface DebtorAccount {
  ispb: string;
  name: string;
  issuer: string;
  number: string;
  document: string;
  accountType: string;
}

export interface CreditorAccount {
  ispb: string;
  name: string;
  issuer: string;
  number: string;
  document: string;
  accountType: string;
}

export enum StatusWithdrawal {
  PENDING = 'Pendente',
  PENDING_APPROVAL = 'Aguardando Aprovação',
  IN_PAYMENT = 'Em Pagamento',
  FINISHED = 'Finalizado',
  CANCELED = 'Cancelado',
}

const NEUTRAL_STATUS = { colorScheme: 'gray', variant: 'subtle' } as const;

/**
 * Só o que exige decisão recebe cor forte.
 *
 * Antes todo estado saía amarelo menos Cancelado e Finalizado — inclusive Em Pagamento, que
 * é justamente o "já está em curso, não mexa". Com a tabela inteira colorida, nada saltava.
 */
const STATUS_STYLE = {
  PENDING: { colorScheme: 'orange', variant: 'solid' },
  PENDING_APPROVAL: { colorScheme: 'orange', variant: 'subtle' },
  IN_PAYMENT: { colorScheme: 'blue', variant: 'subtle' },
  FINISHED: NEUTRAL_STATUS,
  CANCELED: { colorScheme: 'red', variant: 'subtle' },
} as const;

const LoteTag: FC<{ batchId?: string | null }> = ({ batchId }) => {
  const navigate = useNavigate();
  if (!batchId) return null;
  return (
    <Badge colorScheme="purple" cursor="pointer" onClick={() => navigate(`/access/withdrawals-batch/${batchId}`)}>
      Lote
    </Badge>
  );
};

const mapType = {
  BILLET: 'Boleto',
  PIX: 'Pix',
  MANU: 'Pix Manual',
  DICT: 'Pix (DICT)',
  QRCODE: 'Pix (Copia e Cola)',
  INTERNAL: 'Interna',
};

export default function Withdrawals() {
  const { updateUserData } = useAuth();
  const toast = useToast();
  const [type, setType] = useState<'BOLETO' | 'MANU' | 'DICT' | 'QRCODE' | 'INTERNAL' | 'FAVORED'>();

  const [cancelingId, setCancelingId] = useState<string | null>(null);

  const handleCancelWithdrawal = (id: string) => {
    if (cancelingId) return;
    setCancelingId(id);
    deleteWithdrawal(id)
      .then(() => {
        queryClient.refetchQueries({
          queryKey: ['findAllWithdrawers'],
          type: 'active',
        });
        updateUserData();
        toast({
          title: 'Sucesso',
          description: 'Saque cancelado com sucesso',
          status: 'success',
          duration: 9000,
          isClosable: true,
          position: 'top',
        });
      })
      .catch((error) => {
        toast({
          title: 'Erro',
          description: error.response.data.message,
          status: 'error',
          duration: 9000,
          isClosable: true,
          position: 'top',
        });
      })
      .finally(() => {
        setCancelingId(null);
      });
  };

  const columns = useMemo(
    () =>
      [
        {
          // Favorecido do Pix ou beneficiário do boleto (ver getWithdrawalRecipient).
          Header: 'Nome',
          accessor: 'CustomerAccounts.accountHolder',
          Cell: ({ row }: any) => {
            return getWithdrawalRecipient(row.original).name ?? '---';
          },
        },
        {
          Header: 'Documento',
          accessor: 'CustomerAccounts.accountHolderDocument',
          Cell: ({ row }: any) => {
            return formatRecipientDocument(getWithdrawalRecipient(row.original).document) ?? '---';
          },
        },
        {
          Header: 'Descrição',
          accessor: 'description',
          Cell: ({ value }: any) => {
            return (
              <Tooltip label={value}>
                <Text
                  overflow="hidden"
                  whiteSpace="nowrap"
                  textOverflow="ellipsis"
                  maxW="120px"
                >
                  {value}
                </Text>
              </Tooltip>
            );
          },
        },
        {
          Header: 'Tipo',
          accessor: 'type',
          Cell: ({ value }) => {
            return mapType[value as keyof typeof mapType];
          },
        },
        {
          Header: 'Valor do Saque',
          accessor: 'amount',
          numeric: true,
          Cell: ({ value }) => {
            return maskCurrency(+value);
          },
        },
        {
          Header: 'Taxa',
          accessor: 'fee',
          numeric: true,
          Cell: ({ value, row }: any) => {
            if (row.original?.type === 'PERCENTUAL') {
              return `${value}%`;
            }

            return maskCurrency(+value);
          },
        },

        {
          Header: 'Status',
          accessor: 'status',
          filter: true,
          values: Object.entries(StatusWithdrawal).map(([value, label]) => ({
            value,
            label,
          })),
          Cell: ({ value }) => {
            const { colorScheme, variant } = STATUS_STYLE[value as keyof typeof STATUS_STYLE] ?? NEUTRAL_STATUS;

            return (
              <Badge colorScheme={colorScheme} variant={variant}>
                {StatusWithdrawal[value as keyof typeof StatusWithdrawal]}
              </Badge>
            );
          },
        },
        {
          Header: 'Lote',
          accessor: 'batchId',
          Cell: ({ value }: any) => <LoteTag batchId={value} />,
        },
        {
          Header: 'Ações',
          accessor: 'actions',
        },
      ] as Column[],
    [],
  );

  const actions = (row: Withdrawal) => (
    <ButtonGroup key={row.id}>
      <SeeWithdrawel data={row}></SeeWithdrawel>
      {row.type !== 'QRCODE' && <EditWithdrawel data={row}></EditWithdrawel>}

      {row.status === 'PENDING' && (
        <Tooltip label="Cancelar este saque e devolver o valor ao saldo">
          <Button
            aria-label="Cancelar Saque"
            size="sm"
            colorScheme="red"
            variant="solid"
            leftIcon={<FaTrash />}
            isLoading={cancelingId === row.id}
            isDisabled={!!cancelingId && cancelingId !== row.id}
            onClick={() => handleCancelWithdrawal(row.id)}
          >
            Cancelar
          </Button>
        </Tooltip>
      )}
    </ButtonGroup>
  );

  const onClose = () => {
    setType(null);
    queryClient.refetchQueries({
      queryKey: ['findAllWithdrawers'],
      type: 'active',
    });
  };

  // Chakra Color Mode
  return (
    <Box>
      <SimpleGrid
        mb="20px"
        columns={{ sm: 1 }}
        spacing={{ base: '20px', xl: '20px' }}
      >
        <TableComponent
          columnsData={columns}
          queryFn={findAllWithdrawers}
          queryKey="findAllWithdrawers"
          label="Saques"
          actions={actions}
          filterable={['createdAt']}
          enableSelect
          aggregateSelection={[{ key: 'amount', label: 'Total selecionado' }]}
          right={
            <>
              <Menu>
                <MenuButton
                  colorScheme="brand"
                  variant="outline"
                  size="sm"
                  as={Button}
                  rightIcon={<ChevronDownIcon />}
                >
                  Solicitar Saque
                </MenuButton>
                <MenuList>
                  <MenuItem onClick={() => setType('DICT')}>Pix por Chave</MenuItem>
                  <MenuItem onClick={() => setType('QRCODE')}>Pix Copia e Cola</MenuItem>
                  {/* <MenuItem onClick={() => setType('MANU')}>Pix Manual</MenuItem> */}
                  <MenuItem onClick={() => setType('BOLETO')}>Boleto</MenuItem>
                  <MenuItem onClick={() => setType('INTERNAL')}>Transferência Interna</MenuItem>
                  <MenuItem onClick={() => setType('FAVORED')}>Salvos</MenuItem>
                </MenuList>
              </Menu>
              {type === 'DICT' && <WithdrawPix onClose={onClose} />}
              {type === 'QRCODE' && <CreatePaymentQrcode onClose={onClose} />}
              {type === 'BOLETO' && <BarcodePayment onClose={onClose} />}
              {type === 'INTERNAL' && <TransferInternal onClose={onClose} />}
              {type === 'MANU' && <CreateWithdrawel onClose={onClose} />}
              {type === 'FAVORED' && <CreateFavored onClose={onClose} />}
            </>
          }
        />
      </SimpleGrid>
    </Box>
  );
}
